import { boolean, mixed, number, object, string } from 'yup';
import { useMemo } from 'react';
import {
  toRepliconDate,
  dateToMidnightUTCString
} from '~/modules/common/dates/convert';
import { getResourceRequestDatesFromProjectDate } from '~/modules/resourcing/common/util';
import { roundToDecimals } from '~/modules/resourcing/common/util/scheduleUtil';
import { getMaxDateForAllocation } from '~/modules/resourcing/components/AllocateResourcePopup/hooks/useAllocateResourcePopupHandler';
import { useGetScheduleHoursForUserAndDateRange } from '~/modules/resourcing/common/hooks/useGetScheduleHoursForUserAndDateRange';
import { useMeContext } from '~/modules/me';
import { DISPLAY_UNIT_ENUM } from '~/modules/resourcing/common/enums';
import { ALLOCATION_PERIOD } from '../../AllocationByPeriod';

const allocationPeriodFieldSchema = period =>
  mixed().when('selectedAllocationPeriod', {
    is: period,
    then: schema =>
      schema.test(
        'non-zero',
        'allocationByPeriod.enterAnAmountError',
        v => Boolean(v) && Number(v) > 0
      )
  });

const validationSchema = object().shape({
  startDate: object()
    .shape({
      year: number().required(),
      month: number().required(),
      day: number().required()
    })
    .required(),
  endDate: object()
    .shape({
      year: number().required(),
      month: number().required(),
      day: number().required()
    })
    .required(),
  isClearAllocationChecked: boolean(),
  selectedAllocationPeriod: string()
    .oneOf(Object.values(ALLOCATION_PERIOD))
    .required(),
  hoursForEntirePeriod: allocationPeriodFieldSchema(
    ALLOCATION_PERIOD.ENTIRE_PERIOD
  ),
  hoursPerMonth: allocationPeriodFieldSchema(ALLOCATION_PERIOD.MONTH),
  hoursPerWeek: allocationPeriodFieldSchema(ALLOCATION_PERIOD.WEEK)
});

export const useSetResourceAllocationFormState = ({
  defaultStartDate,
  defaultEndDate,
  project,
  scale,
  userUri,
  savedSelectedAllocationPeriod,
  savedDisplayUnit
} = {}) => {
  const { resourcePlanningLimitInYears } = useMeContext();

  const projectDates =
    defaultStartDate && defaultEndDate
      ? null
      : getResourceRequestDatesFromProjectDate({
          projectStartDate: project?.startDate,
          projectEndDate: project?.endDate,
          scale
        });

  const resolvedStartDate = defaultStartDate || projectDates?.requestStartDate;
  const resolvedEndDate = defaultEndDate || projectDates?.requestEndDate;

  const maxEndDate = getMaxDateForAllocation({
    startDate: resolvedStartDate,
    endDate: resolvedEndDate,
    resourcePlanningLimitInYears
  });

  const {
    scheduledHours,
    loading: isScheduleLoading
  } = useGetScheduleHoursForUserAndDateRange({
    userUri,
    dateRange: {
      startDate: dateToMidnightUTCString(resolvedStartDate),
      endDate: dateToMidnightUTCString(maxEndDate)
    },
    skip: !userUri
  });

  const totalScheduledHours = scheduledHours || 0;

  const initialValues = useMemo(
    () => ({
      startDate: toRepliconDate(resolvedStartDate),
      endDate: toRepliconDate(maxEndDate),
      isClearAllocationChecked: false,
      selectedAllocationPeriod:
        savedSelectedAllocationPeriod || ALLOCATION_PERIOD.ENTIRE_PERIOD,
      hoursForEntirePeriod:
        totalScheduledHours > 0 ? roundToDecimals(totalScheduledHours) : 0,
      percentageForEntirePeriod: 100,
      hoursPerMonth: '',
      hoursPerWeek: '',
      displayUnit: savedDisplayUnit || DISPLAY_UNIT_ENUM.HOURS
    }),
    [
      resolvedStartDate,
      maxEndDate,
      totalScheduledHours,
      savedSelectedAllocationPeriod,
      savedDisplayUnit
    ]
  );

  return {
    initialValues,
    validationSchema,
    totalScheduledHours,
    isScheduleLoading
  };
};
